import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../style/onboarding2.css";

export default function OnboardingStep1() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("website"); // "website" | "topic"
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // keep onboarding background consistent
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#ffffff";
    return () => (document.body.style.backgroundColor = prev);
  }, []);

  // persist brand in a single, predictable shape
  function saveBrandToLocalStorage(brand) {
    const safe = {
      businessName: brand?.businessName || "",
      description: brand?.description || "",
      favicon: brand?.favicon || "",
      brandColors: Array.isArray(brand?.brandColors) ? brand.brandColors : ["#0066FF", "#000000", "#FFFFFF"],
      sourceUrl: brand?.sourceUrl || "",
    };
    localStorage.setItem("onboarding.brand", JSON.stringify(safe));
  }

  async function handleNext() {
    setErr("");

    // TOPIC MODE — create a bare brand and a topic string
    if (activeTab === "topic") {
      const topic = (inputValue || "").trim();
      if (!topic) {
        setErr("Please enter a topic.");
        return;
      }

      const topicBrand = {
        businessName: topic,
        description: `Content plan for topic: ${topic}`,
        favicon: "",
        brandColors: ["#0066FF", "#000000", "#FFFFFF"],
        sourceUrl: "",
      };

      saveBrandToLocalStorage(topicBrand);
      localStorage.setItem("onboarding.topic", topic);

      // carry to step-2 so the user can still tweak
      navigate("/onboarding/step-2", { state: { from: "topic", ...topicBrand } });
      return;
    }

    // WEBSITE MODE — try scraping, fall back to manual
    const url = (inputValue || "").trim();
    if (!url) {
      setErr("Please enter a website address or switch to Topic.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get("http://localhost:6955/api/brand/scrape", { params: { url } });
      const scraped = {
        businessName: data?.businessName || "",
        description: data?.description || "",
        favicon: data?.favicon || "",
        brandColors: Array.isArray(data?.brandColors) ? data.brandColors : ["#0066FF", "#000000", "#FFFFFF"],
        sourceUrl: url,
      };
      saveBrandToLocalStorage(scraped);
      // optional: clear topic if coming from earlier attempt
      localStorage.removeItem("onboarding.topic");

      navigate("/onboarding/step-2", { state: { from: "website", ...scraped } });
    } catch (e) {
      console.error(e);
      setErr("Couldn’t fetch details from that URL. You can continue and fill details manually.");

      const manual = {
        businessName: "",
        description: "",
        favicon: "",
        brandColors: ["#0066FF", "#000000", "#FFFFFF"],
        sourceUrl: url,
      };
      saveBrandToLocalStorage(manual);
      navigate("/onboarding/step-2", { state: { from: "website", ...manual } });
    } finally {
      setLoading(false);
    }
  }

  function handleNoWebsite() {
    const blank = {
      businessName: "",
      description: "",
      favicon: "",
      brandColors: ["#0066FF", "#000000", "#FFFFFF"],
      sourceUrl: "",
    };
    saveBrandToLocalStorage(blank);
    navigate("/onboarding/step-2", { state: { from: "no-website", ...blank } });
  }

  return (
    <div className="page-wrap">
      <aside className="left-panel" aria-hidden="false">
        <div className="left-card">
          <h1 className="title">Generate Content<br/>instantly with<br/>Clikke Creator</h1>
          <p className="subtitle">
            Clikke Creator can quickly learn about your business, product, or idea and import your brand’s voice, logo and colors.
          </p>
        </div>
      </aside>

      <main className="right-panel flex items-center justify-center">
        <div className="form-wrapper" style={{ width: "100%", maxWidth: 600 }}>
          <form className="form-area" onSubmit={(e) => e.preventDefault()}>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <button type="button"
                onClick={() => setActiveTab("website")}
                style={{
                  flex: 1, borderRadius: 12, padding: "12px 0",
                  background: activeTab === "website" ? "#eaf2ff" : "#f5f6f8",
                  border: "1px solid #e5e7eb", color: activeTab === "website" ? "#000" : "#6b7280",
                  fontWeight: 600, cursor: "pointer",
                }}>
                Website
              </button>
              <button type="button"
                onClick={() => setActiveTab("topic")}
                style={{
                  flex: 1, borderRadius: 12, padding: "12px 0",
                  background: activeTab === "topic" ? "#eaf2ff" : "#f5f6f8",
                  border: "1px solid #e5e7eb", color: activeTab === "topic" ? "#000" : "#6b7280",
                  fontWeight: 600, cursor: "pointer",
                }}>
                Topic
              </button>
              <button type="button" disabled
                style={{ flex: 1, borderRadius: 12, padding: "12px 0", background: "#f5f6f8", border: "1px solid transparent", color: "#9ca3af", fontWeight: 600 }}>
                —
              </button>
            </div>

            {activeTab === "website" ? (
              <>
                <label className="field-label" style={{ marginTop: 0 }}>Website / Link</label>
                <input
                  className="input"
                  placeholder="Enter your website address, Amazon link or any specific topic"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  style={{ borderRadius: 10, border: "1px solid #e5e7eb", padding: "12px 16px", fontSize: 15, width: "100%", background: "#fff" }}
                />
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                  Examples: <u>www.example.com</u>, <u>Holiday Blogs</u>, <u>Womans Necklace</u>
                </div>
              </>
            ) : (
              <>
                <label className="field-label" style={{ marginTop: 0 }}>Topic</label>
                <textarea
                  className="textarea"
                  placeholder="Describe the content you want (e.g., 'Write about why AI saves time for HR teams')"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  style={{ borderRadius: 10, border: "1px solid #e5e7eb", padding: "12px 16px", fontSize: 15, width: "100%", minHeight: 80, background: "#fff" }}
                />
              </>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 24 }}>
              <button type="button" onClick={handleNoWebsite} className="btn linklike" style={{ color: "#0066ff", fontWeight: 500 }}>
                I have a business without a website
              </button>
              <button type="button" onClick={handleNext} disabled={loading} className="btn primary-pill"
                style={{ minWidth: 90, opacity: loading ? 0.6 : 1, background: "#0066ff", border: "none", color: "#fff",
                         borderRadius: 999, padding: "10px 22px", fontWeight: 600, cursor: "pointer" }}>
                {loading ? "Fetching..." : "Next"}
              </button>
            </div>

            {err && <div style={{ marginTop: 10, fontSize: 12, color: "#dc2626" }}>{err}</div>}
          </form>
        </div>
      </main>
    </div>
  );
}
